/**
 * Recording, and withdrawing, the firm's own FICA check for one person on a
 * trust's register.
 *
 * RULING 2026-09-08 (Paul): manual capture, option A. Until this shipped,
 * nothing anywhere in the product could put an initial FICA on file, so every
 * trust read "Initial FICA outstanding" forever and no periodic review clock
 * could ever start. See `tasks/DECISION-fica-capture-20260907.md`.
 *
 * TWO THINGS THIS SCREEN OWES THE PERSON USING IT.
 *
 * 1. It must never let the firm think it has done something it has not. The
 *    dialog says in plain words that this is the firm's own record of documents
 *    it has seen, not a verification by anybody else, and the register then
 *    shows it as exactly that, with the operator's name on it.
 * 2. The two parts are ticked separately and both are required. A FICA file
 *    with an identity document and no proof of address is not a FICA file, and
 *    the button says why it is disabled rather than sitting there greyed out
 *    with no explanation (design gate C3).
 */

import { useEffect, useState } from 'react';
import { Modal } from '../ui/modal';
import { useRecordFicaCheck, useWithdrawFicaCheck } from '../../lib/api/beneficial-ownership';
import { mapError } from '../../lib/error-messages';
import { displayPersonName } from '../../lib/format/person-name';

export interface FicaCheckSubject {
  id: string;
  name: string;
  /** The deed word already mapped, e.g. "Founder". Never a raw category. */
  role: string;
}

function checkboxRow(props: {
  testId: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint: string;
  disabled: boolean;
}) {
  return (
    <label
      key={props.testId}
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
        padding: '10px 0',
        cursor: props.disabled ? 'default' : 'pointer',
      }}
    >
      <input
        type="checkbox"
        data-testid={props.testId}
        checked={props.checked}
        disabled={props.disabled}
        onChange={(e) => props.onChange(e.target.checked)}
        style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0 }}
      />
      <span>
        <span style={{ display: 'block', fontSize: 13.5, color: 'var(--ink)' }}>{props.label}</span>
        <span style={{ display: 'block', fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
          {props.hint}
        </span>
      </span>
    </label>
  );
}

export function RecordFicaCheckDialog({
  open,
  onClose,
  trustId,
  subject,
}: {
  open: boolean;
  onClose: () => void;
  trustId: string;
  subject: FicaCheckSubject | null;
}) {
  const [identitySeen, setIdentitySeen] = useState(false);
  const [addressSeen, setAddressSeen] = useState(false);
  const record = useRecordFicaCheck(trustId);

  // A fresh dialog per person. Carrying the last person's ticks into the next
  // one is how a wrong confirmation gets recorded in two clicks.
  useEffect(() => {
    if (open) {
      setIdentitySeen(false);
      setAddressSeen(false);
      record.reset();
    }
    // `record` is a stable mutation object from react-query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, subject?.id]);

  if (!subject) return null;
  const both = identitySeen && addressSeen;

  return (
    <Modal open={open} onClose={onClose} title="Record a FICA check" size="md">
      <div style={{ display: 'grid', gap: 10 }}>
        <p style={{ margin: 0, fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.55 }}>
          For <strong>{displayPersonName(subject.name)}</strong>, {subject.role.toLowerCase()} on this trust.
        </p>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.55 }}>
          This records what your firm has seen. It is your own attestation, kept
          with your name and today’s date against it, and it is not a
          verification by an identity provider. Confirm only what is in front of
          you.
        </p>

        <div style={{ borderTop: '1px solid var(--hairline)' }}>
          {checkboxRow({
            testId: 'fica-check-identity',
            checked: identitySeen,
            onChange: setIdentitySeen,
            label: 'I have seen the identity document',
            hint: 'A South African identity document, card or passport for this person.',
            disabled: record.isPending,
          })}
          {checkboxRow({
            testId: 'fica-check-address',
            checked: addressSeen,
            onChange: setAddressSeen,
            label: 'I have seen the proof of address',
            hint: 'A document under three months old showing where this person lives.',
            disabled: record.isPending,
          })}
        </div>

        {!both && (
          <p
            data-testid="fica-check-incomplete-note"
            style={{ margin: 0, fontSize: 12, color: 'var(--ink-3)' }}
          >
            Both are needed before this can be recorded. Leave this person
            outstanding if you are missing one.
          </p>
        )}

        {record.isError && (
          <p
            data-testid="fica-check-error"
            style={{ margin: 0, fontSize: 12.5, color: 'var(--negative)', lineHeight: 1.5 }}
          >
            {mapError(record.error, 'save')}
          </p>
        )}
      </div>

      <Modal.Footer>
        <button className="btn-secondary" onClick={onClose} disabled={record.isPending}>
          Cancel
        </button>
        <button
          className="btn-primary"
          data-testid="fica-check-submit"
          disabled={!both || record.isPending}
          onClick={() => {
            record.mutate(
              {
                id: subject.id,
                body: { identityDocumentSeen: identitySeen, addressProofSeen: addressSeen },
              },
              { onSuccess: () => onClose() },
            );
          }}
        >
          {record.isPending ? 'Recording…' : 'Record FICA check'}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export function WithdrawFicaCheckDialog({
  open,
  onClose,
  trustId,
  subject,
}: {
  open: boolean;
  onClose: () => void;
  trustId: string;
  subject: FicaCheckSubject | null;
}) {
  const [reason, setReason] = useState('');
  const withdraw = useWithdrawFicaCheck(trustId);

  useEffect(() => {
    if (open) {
      setReason('');
      withdraw.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, subject?.id]);

  if (!subject) return null;
  const ready = reason.trim().length >= 3;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Withdraw this FICA check"
      size="md"
      accentBorder="var(--negative)"
    >
      <div style={{ display: 'grid', gap: 10 }}>
        <p style={{ margin: 0, fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.55 }}>
          <strong>{displayPersonName(subject.name)}</strong> goes back to outstanding, and this
          trust loses its initial FICA until somebody records it again.
        </p>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.55 }}>
          Nothing is deleted. The original confirmation stays on the record and
          both entries stand on this trust’s audit trail.
        </p>
        <label style={{ display: 'grid', gap: 4 }}>
          <span style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>Why is it being withdrawn?</span>
          <textarea
            data-testid="fica-withdraw-reason"
            value={reason}
            rows={3}
            disabled={withdraw.isPending}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Confirmed against the wrong person"
            style={{
              width: '100%',
              fontSize: 13,
              padding: 8,
              borderRadius: 6,
              border: '1px solid var(--hairline)',
              background: 'var(--surface)',
              color: 'var(--ink)',
              resize: 'vertical',
            }}
          />
        </label>
        {!ready && (
          <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-3)' }}>
            A short reason is needed, so the record says what happened.
          </p>
        )}
        {withdraw.isError && (
          <p
            data-testid="fica-withdraw-error"
            style={{ margin: 0, fontSize: 12.5, color: 'var(--negative)', lineHeight: 1.5 }}
          >
            {mapError(withdraw.error, 'save')}
          </p>
        )}
      </div>
      <Modal.Footer>
        <button className="btn-secondary" onClick={onClose} disabled={withdraw.isPending}>
          Keep it
        </button>
        <button
          className="btn-primary"
          data-testid="fica-withdraw-submit"
          disabled={!ready || withdraw.isPending}
          onClick={() => {
            withdraw.mutate(
              { id: subject.id, reason: reason.trim() },
              { onSuccess: () => onClose() },
            );
          }}
        >
          {withdraw.isPending ? 'Withdrawing…' : 'Withdraw the check'}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
